/** The logger's lap analysis, for the lap chosen in the table.
 *
 * The lap table says how long each lap took; this card says where. Each row is
 * a corner of the circuit with the straight leading into it, and the first
 * number on the row is what that stretch cost against the reference lap the
 * logger chose — the pieces add up to the lap's gap, so the bars are the gap
 * taken apart. The rest of the row is the why: where the braking started,
 * how slow the corner got, how early the throttle came back.
 *
 * Differences are this lap minus the reference. A positive time is time lost
 * (warn); a positive speed is speed gained (ok). A lap the logger could not
 * line up with the reference keeps its own figures and loses its differences,
 * and the card says so rather than drawing bars nobody should trust.
 */

import type { ReactNode } from 'react';
import { Card, ErrorNote, Kicker, Spinner, TBody, TD, TH, THead, TR, Table } from '@/components/ui';
import { DASH, count, delta, lapTime, percent } from '@/lib/format';
import { speedIn, speedLabel } from '@/lib/units';
import { useSpeedUnit } from '@/lib/useSpeedUnit';
import {
  analysisLap,
  comparable,
  cornerName,
  cornerRows,
  lossScale,
  worstCorners,
  type AnalysisLap,
  type CornerRow,
  type LapAnalysis,
} from '@/components/sessions/analysis';

export interface AnalysisCardProps {
  lapNumber: number | null;
  document: LapAnalysis | null;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}

const OK = 'var(--color-ok)';
const WARN = 'var(--color-warn)';
const QUIET = 'var(--color-neutral-600)';
const MUTED = 'var(--color-neutral-500)';

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** `+4.2` / `−4.2`, for speeds and distances. */
function signed(value: number | null | undefined, digits = 1): string {
  if (!isNumber(value)) return DASH;
  const fixed = Math.abs(value).toFixed(digits);
  if (Number(fixed) === 0) return fixed;
  return `${value > 0 ? '+' : '−'}${fixed}`;
}

/** Time lost is warn and time gained is ok; a few ms either way is neither. */
function timeTone(ms: number | null | undefined): string {
  if (!isNumber(ms) || Math.abs(ms) <= 5) return MUTED;
  return ms > 0 ? WARN : OK;
}

/** Speed gained is ok, speed given away is warn. */
function speedTone(kmh: number | null | undefined): string {
  if (!isNumber(kmh) || Math.abs(kmh) < 0.5) return MUTED;
  return kmh > 0 ? OK : WARN;
}

/** `42 m before`, `12 m after` — distances from the apex as a driver says them. */
function fromApex(metres: number | null | undefined): string {
  if (!isNumber(metres)) return DASH;
  const rounded = Math.round(Math.abs(metres));
  if (rounded === 0) return 'at apex';
  return `${rounded} m ${metres < 0 ? 'before' : 'after'}`;
}

export function AnalysisCard({ lapNumber, document, loading, error, onRetry }: AnalysisCardProps) {
  const lap = analysisLap(document, lapNumber);
  const rows = cornerRows(document, lap);
  const reference = document?.reference ?? null;
  const isReference = lap !== null && reference?.number === lap.number;

  return (
    <Card elevation="none" label="Lap analysis" style={{ padding: 14, gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Kicker>
          analysis · lap {lapNumber ?? DASH}
          {reference?.number !== undefined ? ` vs lap ${reference.number}` : ''}
        </Kicker>
        {reference ? (
          <span style={{ fontSize: 11, color: MUTED }} title={reference.why}>
            reference {lapTime(reference.time_ms)}
            {document?.app_version ? ` · logger ${document.app_version}` : ''}
          </span>
        ) : null}
      </div>

      {error ? (
        <ErrorNote compact error={error} title="could not load the analysis" onRetry={onRetry} />
      ) : !document ? (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: 100 }}>
          {loading ? <Spinner label="loading the analysis" /> : null}
        </div>
      ) : !lap ? (
        <Note>The analysis has nothing for lap {lapNumber ?? DASH}.</Note>
      ) : (
        <>
          {!comparable(lap) ? (
            <Note tone={WARN}>
              This lap could not be lined up with the reference, so its differences are not shown —
              only its own figures.
            </Note>
          ) : isReference ? (
            <Note>
              This is the reference lap ({reference?.why ?? 'the one the others are measured against'}).
              Choose another lap in the table to see where it lost time.
            </Note>
          ) : (
            <WhereTimeWent lap={lap} rows={rows} />
          )}

          <LapFigures lap={lap} />

          {rows.length > 0 ? (
            <CornerTable rows={rows} showDeltas={comparable(lap) && !isReference} />
          ) : (
            <Note>The logger found no corners on this circuit.</Note>
          )}

          <Consistency document={document} />
        </>
      )}
    </Card>
  );
}

function Note({ children, tone = QUIET }: { children: ReactNode; tone?: string }) {
  return <p style={{ margin: 0, fontSize: 12.5, color: tone, textWrap: 'pretty' }}>{children}</p>;
}

/** One sentence: the lap's gap and the corners it mostly came from. */
function WhereTimeWent({ lap, rows }: { lap: AnalysisLap; rows: CornerRow[] }) {
  const worst = worstCorners(rows);
  return (
    <p style={{ margin: 0, fontSize: 13, color: 'var(--color-neutral-300)', textWrap: 'pretty' }}>
      <span className="tabular-nums" style={{ color: timeTone(lap.time_vs_ref) }}>
        {delta(lap.time_vs_ref)} s
      </span>{' '}
      against the reference.{' '}
      {worst.length === 0
        ? 'No corner gave away more than a couple of hundredths.'
        : (
          <>
            Most of it went at{' '}
            {worst.map((row, index) => (
              <span key={row.n}>
                {index > 0 ? (index === worst.length - 1 ? ' and ' : ', ') : ''}
                <strong style={{ fontWeight: 500 }}>{cornerName(row)}</strong>{' '}
                <span className="tabular-nums" style={{ color: WARN }}>
                  {delta(row.lostMs, 2)}
                </span>
              </span>
            ))}
            .
          </>
        )}
    </p>
  );
}

/** The lap as a whole: pedals, aids, speed, fuel, and anything that went wrong. */
function LapFigures({ lap }: { lap: AnalysisLap }) {
  const speedUnit = useSpeedUnit();
  const events = [
    lap.events?.lockup ? `${lap.events.lockup} lock-up${lap.events.lockup === 1 ? '' : 's'}` : null,
    lap.events?.wheelspin ? `${lap.events.wheelspin} wheelspin` : null,
    lap.off_track ? `${lap.off_track} off track` : null,
  ].filter(Boolean);
  const tyres = lap.tyre_temp;

  const figures: Array<[string, ReactNode]> = [
    ['full throttle', percent(lap.pedals?.full_throttle_pct)],
    ['braking', percent(lap.pedals?.full_brake_pct)],
    ['coasting', percent(lap.pedals?.coasting_pct)],
    ['TCS', percent(lap.aids?.tcs_pct)],
    [
      'top speed',
      isNumber(lap.max_speed)
        ? `${count(speedIn(lap.max_speed, speedUnit.unit))} ${speedLabel(speedUnit.unit)}`
        : DASH,
    ],
    ['fuel used', isNumber(lap.fuel?.used) ? `${lap.fuel.used.toFixed(1)}` : DASH],
    [
      'tyres avg °C',
      tyres
        ? (['fl', 'fr', 'rl', 'rr'] as const)
            .map((corner) => (isNumber(tyres[corner]?.avg) ? Math.round(tyres[corner].avg) : DASH))
            .join(' / ')
        : DASH,
    ],
    ['incidents', events.length ? events.join(', ') : 'none'],
  ];

  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: '8px 14px',
        margin: 0,
      }}
    >
      {figures.map(([label, value]) => (
        <div key={label} style={{ minWidth: 0 }}>
          <dt style={{ fontSize: 10.5, color: QUIET }}>{label}</dt>
          <dd className="tabular-nums" style={{ margin: 0, fontSize: 13, color: 'var(--color-text)' }}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A bar either side of a centre line: time lost to the right, gained to the left. */
function LossBar({ ms, scale }: { ms: number | null; scale: number }) {
  const share = ms === null || scale === 0 ? 0 : Math.min(1, Math.abs(ms) / scale);
  return (
    <span
      aria-hidden
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 64,
        height: 8,
        verticalAlign: 'middle',
        marginRight: 8,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: '50%',
          top: -2,
          bottom: -2,
          width: 1,
          background: 'var(--color-divider)',
        }}
      />
      {share > 0 ? (
        <span
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: `${share * 50}%`,
            ...(ms !== null && ms > 0 ? { left: '50%' } : { right: '50%' }),
            background: timeTone(ms),
            borderRadius: 2,
          }}
        />
      ) : null}
    </span>
  );
}

function CornerTable({ rows, showDeltas }: { rows: CornerRow[]; showDeltas: boolean }) {
  const speedUnit = useSpeedUnit();
  const unit = speedLabel(speedUnit.unit);
  const speed = (kmh: number | undefined) =>
    isNumber(kmh) ? count(speedIn(kmh, speedUnit.unit)) : DASH;
  const speedDelta = (kmh: number | undefined) =>
    isNumber(kmh) ? signed(speedIn(kmh, speedUnit.unit)) : DASH;
  const scale = lossScale(rows);

  return (
    <Table
      scroll
      label="Corners"
      style={{ fontSize: 12.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
    >
      <THead>
        <TR>
          <TH>Corner</TH>
          {showDeltas ? <TH numeric>Δ ref</TH> : null}
          <TH numeric>Time</TH>
          <TH numeric>Brake on</TH>
          <TH numeric>{`Min ${unit}`}</TH>
          <TH numeric>Gear</TH>
          <TH numeric>{`Exit ${unit}`}</TH>
          <TH numeric>Throttle on</TH>
          <TH numeric>Lap to lap</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => {
          const corner = row.lap;
          const braking = corner?.braking;
          const cornerSpeed = corner?.speed;
          const throttle = corner?.throttle;
          return (
            <TR key={row.n}>
              <TD color={MUTED} title={row.angle !== null ? `${Math.round(row.angle)}° turn` : undefined}>
                {cornerName(row)}
              </TD>
              {showDeltas ? (
                <TD
                  numeric
                  color={timeTone(row.lostMs)}
                  title={
                    corner?.approach?.time_vs_ref !== undefined
                      ? `corner ${delta(corner.time_vs_ref)} · straight into it ${delta(corner.approach.time_vs_ref)}`
                      : undefined
                  }
                >
                  <LossBar ms={row.lostMs} scale={scale} />
                  {delta(row.lostMs)}
                </TD>
              ) : null}
              <TD numeric>{isNumber(corner?.time_ms) ? (corner.time_ms / 1000).toFixed(2) : DASH}</TD>
              <TD numeric>
                {braking?.none ? (
                  <span style={{ color: QUIET }}>no brake</span>
                ) : (
                  fromApex(braking?.on_from_apex_m)
                )}
                {showDeltas && isNumber(braking?.on_vs_ref) ? (
                  <span style={{ color: QUIET, marginLeft: 6 }}>
                    {signed(braking.on_vs_ref, 0)} m
                  </span>
                ) : null}
              </TD>
              <TD numeric>
                {speed(cornerSpeed?.min)}
                {showDeltas ? (
                  <span style={{ color: speedTone(cornerSpeed?.min_vs_ref), marginLeft: 6 }}>
                    {speedDelta(cornerSpeed?.min_vs_ref)}
                  </span>
                ) : null}
              </TD>
              <TD numeric color={MUTED}>
                {cornerSpeed?.min_gear ?? DASH}
              </TD>
              <TD numeric>
                {speed(cornerSpeed?.exit)}
                {showDeltas ? (
                  <span style={{ color: speedTone(cornerSpeed?.exit_vs_ref), marginLeft: 6 }}>
                    {speedDelta(cornerSpeed?.exit_vs_ref)}
                  </span>
                ) : null}
              </TD>
              <TD numeric>
                {throttle?.flat ? <span style={{ color: QUIET }}>flat</span> : fromApex(throttle?.on_from_apex_m)}
              </TD>
              <TD numeric color={MUTED} title="σ of this corner's time over the session's clean laps">
                {isNumber(row.timeStd) ? `±${(row.timeStd / 1000).toFixed(2)} s` : DASH}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

/** How repeatable the session was, from the logger's own count of clean laps. */
function Consistency({ document }: { document: LapAnalysis }) {
  const lapTimes = document.consistency?.lap_time;
  if (!lapTimes || !isNumber(lapTimes.std_ms)) return null;
  return (
    <p className="tabular-nums" style={{ margin: 0, fontSize: 11.5, color: QUIET }}>
      Session: median {lapTime(lapTimes.median_ms)}, σ {(lapTimes.std_ms / 1000).toFixed(2)} s
      {isNumber(lapTimes.pct) ? ` (${percent(lapTimes.pct)})` : ''} over{' '}
      {count(lapTimes.laps)} laps. Brake points are from the apex; differences are this lap minus the
      reference.
    </p>
  );
}

export default AnalysisCard;
